// ════════════════════════════════════════════════════════════════════════════
// THE TYPED VALUE CODECS — how a number stays a number across the block.
//
// A block is text. A widget needs `rating === 4.8`, not `"4.8/5 498 reviews"`.
// These are the paired write/read functions that make the crossing lossless,
// and they exist HERE, once, for the same reason `map-constants.ts` holds the
// map's separator beside the map's regexes: the side that prints a value and
// the side that reads it back cannot be allowed to hold two opinions about
// what the printing means.
//
// Every codec is a PAIR and every pair round-trips: `read(write(x)) === x` for
// every value the pillars' captures actually hold, which the suites assert on
// real captured records rather than on hand-written examples.
//
// ─── WHAT MAY BE WRITTEN, AND WHAT MAY NOT ──────────────────────────────────
//
// A SUB-RECORD is a fold, and D21's rule on folds is unchanged: only
// CONSTRAINED values fold. An airport code, an ISO date, a 24-hour clock, an
// `XX NNN` flight number, a cabin name, an aircraft name, an OTA name, a
// number, a flag — those go inside a sub-record. FREE TEXT (a title, a
// snippet, a guest's own words, a room name) NEVER does: it takes its own
// field line, where nothing can collide with it. `attachSubRecordSafe` is the
// mechanical half of that rule, so a violation is a reported fact rather than
// a value that silently re-splits on read.
// ════════════════════════════════════════════════════════════════════════════

import type { AttachFieldLine, AttachItemBlock } from '../grammar.js';

/** What joins the parts of one sub-record. The map's own field separator —
 *  `place :: label :: description` — because this grammar already has one
 *  answer to "several named values on one line" and a second spelling would be
 *  a second grammar to keep in step. */
export const ATTACH_PART_SEPARATOR = ' :: ';

/** What separates a part's name from its value: the same `key: value` the
 *  grammar's field lines and directives use, so there is ONE shape for "a
 *  named value" at every level of the block. */
export const ATTACH_PART_ASSIGN = ': ';

/** A part's name is read up to the FIRST colon, so a name can hold no colon —
 *  the same constraint `ATTACH_FIELD_LINE_REGEX` puts on a field key, and for
 *  the same reason: the value keeps every colon it was captured with
 *  (`time: 11:25` is the part `time`, worth `11:25`). */
const PART_ASSIGN_CHARACTER = ':';
const PART_ASSIGN_INDEX_MISSING = -1;

/** A whole decimal, negative or not. `Number('')` is 0 and `Number(' ')` is 0,
 *  so a shape test comes FIRST and the coercion only ever runs on something
 *  that already reads as a number. */
const ATTACH_NUMBER_REGEX = /^-?\d+(?:\.\d+)?$/;

/** `lat, lon`, exactly as the map writes a literal-coordinate pin. */
const ATTACH_COORDINATE_REGEX = /^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/;
const ATTACH_COORDINATE_SEPARATOR = ', ';

/** The map's own bounds, restated rather than imported: `map-constants.ts`
 *  lives under `business-logic/` and this file is domain. `91, 181` is text,
 *  not a coordinate — the same call `parseCoordinatePin` makes. */
const ATTACH_LAT_MIN = -90;
const ATTACH_LAT_MAX = 90;
const ATTACH_LON_MIN = -180;
const ATTACH_LON_MAX = 180;

/** The two words a flag is written as. A flag is only ever WRITTEN when the
 *  record actually holds one — an absent flag prints no line, which is silence
 *  and not a denial (D22). */
export enum AttachFlagText {
  Yes = 'yes',
  No = 'no',
}

/** A point, as the block carries one. Named `lat`/`lon` after the map's own
 *  `MapPinCoordinates`, so a reader who has met one has met both. */
export interface AttachCoordinates {
  readonly lat: number;
  readonly lon: number;
}

// ─── scalars ────────────────────────────────────────────────────────────────

/** A number as the block prints it. `String` and not `toLocaleString`: a
 *  grouped `1,234` is a READING of a number, and a reading cannot be read back
 *  without knowing which locale wrote it. Display grouping is the renderer's,
 *  from the number this carries. */
export function attachNumberText(input: { value: number }): string | null {
  return Number.isFinite(input.value) ? String(input.value) : null;
}

/** The number a value states, or null. Null for anything that is not exactly a
 *  decimal — a persisted block never yields NaN, and a value that merely
 *  contains digits (`4.8/5`) is not a number this grammar wrote. */
export function attachNumberOf(input: { value: string | null }): number | null {
  const text = input.value === null ? '' : input.value.trim();
  if (!ATTACH_NUMBER_REGEX.test(text)) return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/** `35.0009321, 135.7784988` — both halves, in range, or nothing. */
export function attachCoordinatesText(input: {
  value: AttachCoordinates;
}): string | null {
  const lat = attachNumberText({ value: input.value.lat });
  const lon = attachNumberText({ value: input.value.lon });
  return lat === null || lon === null ? null : `${lat}${ATTACH_COORDINATE_SEPARATOR}${lon}`;
}

export function attachCoordinatesOf(input: {
  value: string | null;
}): AttachCoordinates | null {
  const match = (input.value ?? '').trim().match(ATTACH_COORDINATE_REGEX);
  if (match === null) return null;
  const lat = Number(match[1]);
  const lon = Number(match[2]);
  const inRange =
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    lat >= ATTACH_LAT_MIN &&
    lat <= ATTACH_LAT_MAX &&
    lon >= ATTACH_LON_MIN &&
    lon <= ATTACH_LON_MAX;
  return inRange ? { lat, lon } : null;
}

export function attachFlagText(input: { value: boolean }): string {
  return input.value ? AttachFlagText.Yes : AttachFlagText.No;
}

/** The flag a value states, or null when the block stated none. Null is
 *  "nothing was said", which is a third answer and never folded into `false`. */
export function attachFlagOf(input: { value: string | null }): boolean | null {
  const text = (input.value ?? '').trim().toLowerCase();
  if (text === AttachFlagText.Yes) return true;
  if (text === AttachFlagText.No) return false;
  return null;
}

/** A non-empty string, or null. An empty string is absence wearing a value —
 *  the same read the projector lanes already share. */
export function attachTextOf(input: { value: string | null | undefined }): string | null {
  const value = input.value;
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

// ─── sub-records ────────────────────────────────────────────────────────────

/** One sub-record, read. The parts keep the order they were written in, and a
 *  part name may repeat — the same tolerance the field lines above them have,
 *  for the same reason. */
export interface AttachSubRecord {
  readonly parts: readonly AttachFieldLine[];
}

/** One part a writer wants in a sub-record, before absence is resolved. */
export interface AttachPartDraft {
  readonly key: string;
  readonly value: string | null | undefined;
}

/**
 * Whether a value can survive a sub-record round trip.
 *
 * A value carrying the part separator would re-split on read into two parts
 * that were never written. This is the mechanical half of D21's constrained-
 * values-only rule, and it is EXPORTED so a suite can state, over the real
 * captured population, that nothing a pillar writes trips it — a field that
 * does is reported, never quietly dropped.
 */
export function attachSubRecordSafe(input: { value: string }): boolean {
  return !input.value.includes(ATTACH_PART_SEPARATOR);
}

/**
 * One sub-record as the block prints it, or null when no part survived.
 *
 * Absent parts print nothing at all rather than an empty name — a `wifi:` with
 * nothing after it would state that we looked and found nothing, which is a
 * different claim from not knowing. An UNSAFE value is dropped here and the
 * drop is visible to `attachSubRecordSafe`'s own census, which is what keeps
 * the two halves of the rule in one place.
 */
export function attachSubRecordText(input: {
  parts: readonly AttachPartDraft[];
}): string | null {
  const written = input.parts.flatMap((part) => {
    const value = attachTextOf({ value: part.value });
    if (value === null || !attachSubRecordSafe({ value })) return [];
    return [`${part.key}${ATTACH_PART_ASSIGN}${value}`];
  });
  return written.length === 0 ? null : written.join(ATTACH_PART_SEPARATOR);
}

/** A sub-record's parts, read back. A segment with no assignment at all is not
 *  a part and is skipped — never guessed at as a positional value. */
export function attachSubRecordOf(input: { value: string | null }): AttachSubRecord {
  const value = input.value === null ? '' : input.value;
  const parts = value
    .split(ATTACH_PART_SEPARATOR)
    .flatMap<AttachFieldLine>((segment) => {
      const index = segment.indexOf(PART_ASSIGN_CHARACTER);
      if (index === PART_ASSIGN_INDEX_MISSING) return [];
      const key = segment.slice(0, index).trim();
      if (key.length === 0) return [];
      return [{ key, value: segment.slice(index + PART_ASSIGN_CHARACTER.length).trim() }];
    });
  return { parts };
}

/** One part's value, as text. */
export function attachPartText(input: {
  record: AttachSubRecord;
  key: string;
}): string | null {
  const part = input.record.parts.find((candidate) => candidate.key === input.key);
  return part === undefined ? null : attachTextOf({ value: part.value });
}

export function attachPartNumber(input: {
  record: AttachSubRecord;
  key: string;
}): number | null {
  return attachNumberOf({ value: attachPartText(input) });
}

export function attachPartFlag(input: {
  record: AttachSubRecord;
  key: string;
}): boolean | null {
  return attachFlagOf({ value: attachPartText(input) });
}

// ─── grouped repeated keys ──────────────────────────────────────────────────

/** One group: the line that opened it, and the member lines that followed it
 *  before the next opener. */
export interface AttachFieldGroup {
  readonly open: string;
  readonly members: readonly AttachFieldLine[];
}

/**
 * Repeated keys read as GROUPS rather than as one flat run.
 *
 * The grammar already writes a record's follow-on lines adjacent to the line
 * they belong to — a hotels `book:` under its own `offer:`, a flights
 * `fare includes:` under its own `fare:` — and that adjacency is the ratified
 * form (2026-08-19/20). This is the READING half of it, here rather than in
 * each consumer, because a consumer pairing them itself would be a second
 * reading of the grammar: the drift the one-parser rule exists to forbid.
 *
 * A member line before any opener has nothing to belong to and is skipped —
 * the same call the parser makes for a field line before any item. Keys are
 * matched exactly as the block prints them.
 */
export function attachFieldGroups(input: {
  item: AttachItemBlock;
  openKey: string;
  memberKeys: readonly string[];
}): AttachFieldGroup[] {
  const memberKeys = new Set(input.memberKeys);
  return input.item.fields.reduce<{ open: string; members: AttachFieldLine[] }[]>(
    (groups, field) => {
      if (field.key === input.openKey) {
        return [...groups, { open: field.value, members: [] }];
      }
      const current = groups[groups.length - 1];
      if (current !== undefined && memberKeys.has(field.key)) {
        current.members.push(field);
      }
      return groups;
    },
    [],
  );
}

/** Every member value under `key` inside one group, in written order. */
export function attachGroupValues(input: {
  group: AttachFieldGroup;
  key: string;
}): string[] {
  return input.group.members
    .filter((member) => member.key === input.key)
    .map((member) => member.value);
}

/** The first member value under `key` inside one group, or null. */
export function attachGroupValue(input: {
  group: AttachFieldGroup;
  key: string;
}): string | null {
  return attachGroupValues(input)[0] ?? null;
}
